import { useEffect, useState } from "react";
import { X, Download, RefreshCw, Share, Plus } from "lucide-react";
import { onServiceWorkerUpdate, applyServiceWorkerUpdate } from "@/serviceWorkerRegistration";

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}
function isInStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function PwaControls() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [update, setUpdate] = useState(null);

  useEffect(() => {
    if (isInStandalone()) return; // already installed
    const dismissed = sessionStorage.getItem("vf-install-dismissed") === "1";

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!dismissed) setShowInstall(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS has no beforeinstallprompt — offer manual Add to Home Screen guidance.
    if (isIos() && !dismissed) {
      const t = setTimeout(() => setShowIosHelp(true), 4000);
      window.addEventListener("beforeinstallprompt", onBeforeInstall);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  useEffect(() => {
    onServiceWorkerUpdate(({ waitingWorker }) => setUpdate({ waitingWorker }));
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice.catch(() => {});
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  const dismissInstall = () => {
    sessionStorage.setItem("vf-install-dismissed", "1");
    setShowInstall(false);
    setShowIosHelp(false);
  };

  return (
    <>
      {showInstall && (
        <div
          data-testid="pwa-install-banner"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-[#141B2E] border border-[rgba(200,169,106,0.35)] rounded-xl shadow-2xl p-4 flex items-center gap-4"
        >
          <span className="font-stencil text-xs text-[#0B101C] bg-clinical px-2 py-1.5 rounded-md shrink-0">VF</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-ink font-medium">Install VetForge</p>
            <p className="text-xs text-[#8E96AB]">Add to your home screen for faster access.</p>
          </div>
          <button
            onClick={install}
            data-testid="pwa-install-button"
            className="inline-flex items-center gap-1.5 bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.15em] font-semibold px-4 py-2 rounded-lg hover:bg-[#DBC28C] transition-colors duration-300 shrink-0"
          >
            <Download className="h-3.5 w-3.5" /> Install
          </button>
          <button onClick={dismissInstall} aria-label="Dismiss" className="text-[#8E96AB] hover:text-ink transition-colors shrink-0" data-testid="pwa-install-dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {showIosHelp && (
        <div
          data-testid="pwa-ios-banner"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-[#141B2E] border border-[rgba(200,169,106,0.35)] rounded-xl shadow-2xl p-4 space-y-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="font-stencil text-xs text-[#0B101C] bg-clinical px-2 py-1.5 rounded-md shrink-0">VF</span>
              <p className="text-sm text-ink font-medium">Install VetForge on your iPhone</p>
            </div>
            <button onClick={dismissInstall} aria-label="Dismiss" className="text-[#8E96AB] hover:text-ink transition-colors shrink-0" data-testid="pwa-ios-dismiss">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-[#B9BFCE] leading-relaxed">
            Tap the <Share className="inline h-3.5 w-3.5 text-clinical -mt-0.5" /> Share button in Safari, then choose{" "}
            <span className="text-ink inline-flex items-center gap-1"><Plus className="inline h-3 w-3 text-clinical" /> Add to Home Screen</span> to install VetForge like an app.
          </p>
        </div>
      )}

      {update && (
        <div
          data-testid="pwa-update-banner"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-[#141B2E] border border-clinical/50 rounded-xl shadow-2xl p-4 flex items-center gap-4"
        >
          <RefreshCw className="h-5 w-5 text-clinical shrink-0" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-ink font-medium">Update available</p>
            <p className="text-xs text-[#8E96AB]">A new version of VetForge is ready.</p>
          </div>
          <button
            onClick={() => applyServiceWorkerUpdate(update.waitingWorker)}
            data-testid="pwa-update-button"
            className="inline-flex items-center gap-1.5 bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.15em] font-semibold px-4 py-2 rounded-lg hover:bg-[#DBC28C] transition-colors duration-300 shrink-0"
          >
            Refresh
          </button>
        </div>
      )}
    </>
  );
}
